import { useContext } from 'react';
import { media, styled } from '@nx-kit/styling';
import { useQuery } from '@tanstack/react-query';
import getHeadingTag from 'helpers/getHeadingTag';
import { fetchBlogs } from 'prismic/api/blogs';
import { useLinkResolver } from 'prismic/helpers/useLinkResolver';
import { BreakpointContext } from 'contexts/breakpoint/BreakpointContext';
import { DomainContext } from 'contexts/domain/DomainContext';
import { mapEdgeItemToNewsBoxItem } from 'components/molecules/NewsSliderItem/NewsSliderItem.helper';
import { NewsSliderItemProps } from 'components/molecules/NewsSliderItem/NewsSliderItem.types';
import NewsSliderItem from 'components/molecules/NewsSliderItem/NewsSliderItem';
import Container from 'components/layout/Grid/Container';
import Slider from 'components/molecules/Slider/Slider';
import { Heading } from 'components/atoms/Heading';
import Column from 'components/layout/Grid/Column';
import Link from 'components/atoms/Link/Link';
import { Loading } from 'components/atoms/Loading';
import { NewsSliderProps } from './NewsSlider.types';
import useTranslation from 'next-translate/useTranslation';
import getHeadingSkin from 'helpers/getHeadingSkin';

const HeadingStyled = styled(Heading)`
  margin: 0;
`;

const HeadlineCol = styled(Column)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 16px;

  ${media('md')} {
    margin: 0 0 20px;
  }
`;

const ColumnLinkStyled = styled(Column)`
  margin-top: 15px;
`;

const StyledMobileLink = styled(Link)`
  font-size: ${({ theme }) => theme.global.fontSize.medium}px;
`;

const Wrapper = styled(Container)`
  overflow: hidden;
  margin-bottom: -30px;
  padding-bottom: 30px;
`;

export const NewsSlider = ({ className, primary }: NewsSliderProps) => {
  const { t } = useTranslation('common');

  const { headline = null, link = null, link_text = '' } = primary;
  const hasHeadline = headline?.[0]?.text || link;
  const { locale } = useLinkResolver();

  const bp = useContext(BreakpointContext);
  const isMobile = bp === 'xs' || bp === 'sm';

  const {
    state: { domain },
  } = useContext(DomainContext);

  const items: NewsSliderItemProps[] = [];

  const fetchBlogsQuery = async () => {
    return fetchBlogs({ domain, query: `?locale=${locale}&domain=${domain}` });
  };

  const { data, error, isLoading } = useQuery(['blogs'], fetchBlogsQuery);

  const getError = (error: unknown): string => {
    console.error('[NewsSlider]', error);
    return t('apiErrors.errorDefaultMessage');
  };

  if (data?.data?.items) {
    data.data.items.slice(0, 9).forEach((edgeItem) => {
      const item = mapEdgeItemToNewsBoxItem(locale, edgeItem);
      items.push(item);
    });
  }

  return (
    <div className={className}>
      <Wrapper>
        {hasHeadline && (
          <HeadlineCol span={12}>
            {headline?.[0]?.text && (
              <HeadingStyled
                skin={getHeadingSkin(headline[0].type)}
                elementType={getHeadingTag(headline[0].type)}
              >
                {headline[0].text}
              </HeadingStyled>
            )}
            {link && !isMobile && (
              <Link skin="tertiaryBig" url={link}>
                {link_text || link.url}
              </Link>
            )}
          </HeadlineCol>
        )}
        <Column span={12}>
          {error && <div>{getError(error)}</div>}
          {isLoading && <Loading />}

          <Slider
            slidesPerView={{
              xs: 1.05,
              sm: 1.5,
              md: 2,
              lg: 3,
              xl: 3,
              xxl: 3,
              xxxl: 3,
            }}
            showOverflow
          >
            {items.map((item, index) => (
              <NewsSliderItem
                key={`schedule-slider-item-${index}`}
                link={item.link}
                image={item.image}
                text={item.text}
                subtext={item.subtext}
              />
            ))}
          </Slider>
        </Column>
        <ColumnLinkStyled span={12}>
          {link && isMobile && (
            <StyledMobileLink skin="tertiaryBig" url={link}>
              {link_text || link.url}
            </StyledMobileLink>
          )}
        </ColumnLinkStyled>
      </Wrapper>
    </div>
  );
};
